import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Trash2, Plus, FileDown, Radio as RadioIcon, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { SignaturePad } from "@/components/SignaturePad";

const EMPTY = { nominativo: "", radio_id: "none", matricola: "", tipo: "Portatile", modello: "", selettiva: "", accessori: [] };
const COMMON_ACCESSORI = ["Auricolare", "Microfono", "Caricabatterie", "Custodia", "Batteria extra"];

function fmt(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function RadioModuleDialog({ open, onOpenChange, module, radios, onChanged }) {
  const [assignments, setAssignments] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [drafts, setDrafts] = useState({});
  const [notes, setNotes] = useState({});
  const [accInput, setAccInput] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setAssignments(module?.assignments || []);
    setForm(EMPTY);
    setDrafts({});
  }, [module]);

  if (!module) return null;

  const pickRadio = (id) => {
    if (id === "none") { setForm((p) => ({ ...p, radio_id: "none" })); return; }
    const r = radios.find((x) => x.id === id);
    setForm((p) => ({ ...p, radio_id: id, matricola: r?.matricola || "", tipo: r?.tipo || p.tipo, modello: r?.modello || "", selettiva: r?.selettiva || "" }));
  };

  const toggleAcc = (acc) =>
    setForm((p) => ({ ...p, accessori: p.accessori.includes(acc) ? p.accessori.filter((x) => x !== acc) : [...p.accessori, acc] }));
  const addCustomAcc = () => {
    const v = accInput.trim();
    if (v && !form.accessori.includes(v)) setForm((p) => ({ ...p, accessori: [...p.accessori, v] }));
    setAccInput("");
  };

  const addAssignment = async (e) => {
    e.preventDefault();
    if (!form.nominativo.trim()) { toast.error("Inserisci il nominativo"); return; }
    if (!form.matricola.trim()) { toast.error("Inserisci la matricola"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/radio-modules/${module.id}/assignments`, {
        nominativo: form.nominativo, radio_id: form.radio_id === "none" ? null : form.radio_id,
        matricola: form.matricola, tipo: form.tipo, modello: form.modello, selettiva: form.selettiva,
        accessori: form.accessori,
      });
      setAssignments((p) => [...p, data]);
      setForm(EMPTY);
      onChanged?.();
      toast.success("Radio assegnata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveSig = async (a, which) => {
    const key = `${a.id}:${which}`;
    const val = drafts[key];
    if (!val) { toast.error("Firma prima di salvare"); return; }
    try {
      const payload = which === "consegna"
        ? { firma_consegna: val }
        : { firma_riconsegna: val, note_riconsegna: notes[a.id] || "" };
      const { data } = await api.put(`/radio-modules/${module.id}/assignments/${a.id}`, payload);
      setAssignments((p) => p.map((x) => (x.id === a.id ? data : x)));
      setDrafts((p) => { const n = { ...p }; delete n[key]; return n; });
      onChanged?.();
      toast.success(which === "consegna" ? "Firma di consegna salvata" : "Firma di riconsegna salvata");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeAssignment = async (a) => {
    try {
      await api.delete(`/radio-modules/${module.id}/assignments/${a.id}`);
      setAssignments((p) => p.filter((x) => x.id !== a.id));
      onChanged?.();
      toast.success("Assegnazione rimossa");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/radio-modules/${module.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `consegna-radio-${module.event_name}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error("Impossibile generare il PDF");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="radio-module-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><RadioIcon className="h-5 w-5" /> {module.event_name}</DialogTitle>
          <DialogDescription>Assegna le radio ai volontari e raccogli le firme di consegna e riconsegna.</DialogDescription>
          {module.event_date && <p className="text-sm text-muted-foreground">{fmt(module.event_date)}</p>}
        </DialogHeader>

        <div className="flex justify-end">
          <Button size="sm" variant="outline" onClick={downloadPdf} data-testid="download-radio-pdf" className="rounded-full">
            <FileDown className="h-4 w-4 mr-1" /> Scarica PDF
          </Button>
        </div>

        <form onSubmit={addAssignment} className="rounded-xl border border-border p-4 space-y-3" data-testid="add-assignment-form">
          <p className="font-semibold text-sm flex items-center gap-1.5"><Plus className="h-4 w-4" /> Assegna una radio</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Nominativo</Label>
              <Input data-testid="assign-nominativo" value={form.nominativo} onChange={(e) => setForm((p) => ({ ...p, nominativo: e.target.value }))} placeholder="Mario Rossi" />
            </div>
            <div className="space-y-1">
              <Label>Dal parco radio</Label>
              <Select value={form.radio_id} onValueChange={pickRadio}>
                <SelectTrigger data-testid="assign-radio-select"><SelectValue placeholder="Scegli…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Inserimento manuale</SelectItem>
                  {radios.map((r) => (
                    <SelectItem key={r.id} value={r.id} disabled={r.in_use}>{r.matricola} · {r.tipo}{r.modello ? ` · ${r.modello}` : ""}{r.in_use ? " — in uso" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Matricola radio</Label>
              <Input data-testid="assign-matricola" value={form.matricola} onChange={(e) => setForm((p) => ({ ...p, matricola: e.target.value }))} placeholder="R-001" />
            </div>
            <div className="space-y-1">
              <Label>Tipo radio</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm((p) => ({ ...p, tipo: v }))}>
                <SelectTrigger data-testid="assign-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Portatile">Portatile</SelectItem>
                  <SelectItem value="Fissa">Fissa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label>Modello</Label>
              <Input data-testid="assign-modello" value={form.modello} onChange={(e) => setForm((p) => ({ ...p, modello: e.target.value }))} placeholder="Motorola GP340" />
            </div>
            <div className="space-y-1">
              <Label>Selettiva</Label>
              <Input data-testid="assign-selettiva" value={form.selettiva} onChange={(e) => setForm((p) => ({ ...p, selettiva: e.target.value }))} placeholder="101" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Accessori consegnati</Label>
              <div className="flex flex-wrap gap-2">
                {COMMON_ACCESSORI.map((acc) => (
                  <button type="button" key={acc} data-testid={`acc-${acc}`} onClick={() => toggleAcc(acc)}
                    className={`px-3 py-1 rounded-full text-xs border transition-colors ${form.accessori.includes(acc) ? "bg-[#D32F2F] text-white border-[#D32F2F]" : "border-border text-muted-foreground hover:bg-muted"}`}>
                    {acc}
                  </button>
                ))}
                {form.accessori.filter((a) => !COMMON_ACCESSORI.includes(a)).map((acc) => (
                  <button type="button" key={acc} data-testid={`acc-${acc}`} onClick={() => toggleAcc(acc)}
                    className="px-3 py-1 rounded-full text-xs border bg-[#D32F2F] text-white border-[#D32F2F]">
                    {acc} ✕
                  </button>
                ))}
              </div>
              <div className="flex gap-2 pt-1">
                <Input data-testid="acc-custom-input" value={accInput} onChange={(e) => setAccInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustomAcc(); } }} placeholder="Altro accessorio…" className="h-9" />
                <Button type="button" variant="outline" size="sm" data-testid="acc-add-button" onClick={addCustomAcc} className="rounded-full">Aggiungi</Button>
              </div>
            </div>
          </div>
          <Button type="submit" disabled={saving} data-testid="save-assignment-button" className="rounded-full font-semibold">
            {saving ? "Salvataggio…" : "Aggiungi assegnazione"}
          </Button>
        </form>

        <div className="space-y-3">
          <p className="font-semibold text-sm">Radio assegnate ({assignments.length})</p>
          {assignments.length === 0 && <p className="text-sm text-muted-foreground">Nessuna radio assegnata.</p>}
          {assignments.map((a) => (
            <div key={a.id} data-testid={`assignment-${a.id}`} className="rounded-xl border border-border p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="font-semibold">{a.nominativo}</p>
                  <p className="text-xs text-muted-foreground">
                    Matricola <b>{a.matricola}</b> · {a.tipo}{a.modello ? ` · ${a.modello}` : ""}{a.selettiva ? ` · Sel. ${a.selettiva}` : ""}
                  </p>
                  {a.accessori?.length > 0 && (
                    <p className="text-xs text-muted-foreground" data-testid={`assignment-accessori-${a.id}`}>Accessori: {a.accessori.join(", ")}</p>
                  )}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="sm" variant="ghost" data-testid={`delete-assignment-${a.id}`} className="h-8 w-8 p-0 text-destructive hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Rimuovere l'assegnazione di {a.nominativo}?</AlertDialogTitle>
                      <AlertDialogDescription>L'operazione non può essere annullata.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction onClick={() => removeAssignment(a)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Rimuovi</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  {a.firma_consegna ? (
                    <div className="space-y-1">
                      <Badge className="bg-emerald-600 text-white"><Check className="h-3 w-3 mr-1" /> Consegnata</Badge>
                      <p className="text-[11px] text-muted-foreground">{fmt(a.consegna_at)}</p>
                      <img src={a.firma_consegna} alt="firma consegna" className="h-16 border border-border rounded-md bg-white" />
                    </div>
                  ) : (
                    <>
                      <SignaturePad label="Firma di consegna" testid={`sig-consegna-${a.id}`} value={drafts[`${a.id}:consegna`] || ""} onChange={(v) => setDrafts((p) => ({ ...p, [`${a.id}:consegna`]: v }))} />
                      <Button size="sm" onClick={() => saveSig(a, "consegna")} data-testid={`save-consegna-${a.id}`} className="rounded-full">Salva consegna</Button>
                    </>
                  )}
                </div>
                <div>
                  {a.firma_riconsegna ? (
                    <div className="space-y-1">
                      <Badge className="bg-slate-600 text-white"><Check className="h-3 w-3 mr-1" /> Riconsegnata</Badge>
                      <p className="text-[11px] text-muted-foreground">{fmt(a.riconsegna_at)}</p>
                      <img src={a.firma_riconsegna} alt="firma riconsegna" className="h-16 border border-border rounded-md bg-white" />
                      {a.note_riconsegna && <p className="text-[11px] text-amber-700" data-testid={`note-riconsegna-${a.id}`}>Rotture/perdite: {a.note_riconsegna}</p>}
                    </div>
                  ) : a.firma_consegna ? (
                    <>
                      <SignaturePad label="Firma di riconsegna" testid={`sig-riconsegna-${a.id}`} value={drafts[`${a.id}:riconsegna`] || ""} onChange={(v) => setDrafts((p) => ({ ...p, [`${a.id}:riconsegna`]: v }))} />
                      <Textarea data-testid={`note-riconsegna-input-${a.id}`} value={notes[a.id] || ""} onChange={(e) => setNotes((p) => ({ ...p, [a.id]: e.target.value }))} placeholder="Rotture o perdite di accessori (facoltativo)…" className="mt-1 text-sm" rows={2} />
                      <Button size="sm" variant="secondary" onClick={() => saveSig(a, "riconsegna")} data-testid={`save-riconsegna-${a.id}`} className="rounded-full mt-1">Salva riconsegna</Button>
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">La riconsegna sarà disponibile dopo la firma di consegna.</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
